import {useEffect, useRef, useState} from "react";

import fullscreen from "@images/popups/fullscreen.svg";
import pause from "@images/popups/pause.svg";
import play from "@images/popups/play.svg";
import rewind from "@images/popups/rewind.svg";
import settings from "@images/popups/settings.svg";
import sound from "@images/popups/sound.svg";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {useTranslation} from "react-i18next";
import ReactPlayer from "react-player";
import {useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const VideoPlayer = ({
  video,
  playBtnImg,
  playBtnSize = 100,
  controlBar = true,
}) => {
  const {t} = useTranslation();
  const {isMobile} = useSelector((state) => state.common.settings);

  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [preview, setPreview] = useState(true);
  const [volume, setVolume] = useState(0.2);
  const [timeLine, setTimeLine] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [lastPlayedTime, setLastPlayedTime] = useState(0);
  // const [isSeeking, setIsSeeking] = useState(false)

  const volumeRef = useRef(null);
  const timeLineRef = useRef(null);
  const fullscreenRef = useRef(null);
  const playerRef = useRef(null);

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setCurrentTime(videoDuration);
    setTimeLine(1);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  const handleProgress = (progress) => {
    if (isPlaying) {
      setCurrentTime(progress.playedSeconds);
      setTimeLine(progress.playedSeconds / videoDuration);
    }
  };

  const handleDuration = (duration) => {
    setVideoDuration(duration);
  };

  const handleVolumeChange = (event) => {
    setVolume(parseFloat(event.target.value));
  };

  const handleTimeLineChange = (event) => {
    const newTimeLine = parseFloat(event.target.value);

    setTimeLine(newTimeLine);

    if (playerRef.current) {
      const newTime = newTimeLine * videoDuration;

      playerRef.current.seekTo(newTime);
      setCurrentTime(newTime);
    }
  };

  const handlePlay = () => {
    if (!hasStarted) {
      setHasStarted(true);
      setPreview(false);
      setLastPlayedTime(0);
    } else {
      setLastPlayedTime(currentTime);
    }
    setIsPlaying(!isPlaying);
  };

  const handleClickSound = () => {
    setVolume(volume > 0 ? 0 : 1);
  };

  const handleFullscreen = () => {
    const playerContainer = fullscreenRef.current;

    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    } else {
      if (playerContainer.requestFullscreen) {
        playerContainer.requestFullscreen();
      }
    }
  };

  // const handleSeek = () => {
  // 	setIsSeeking(true)
  // 	if (playerRef.current && lastPlayedTime > 0) {
  // 		playerRef.current.seekTo(lastPlayedTime)
  // 	}
  // 	setTimeout(() => {
  // 		setIsSeeking(false)
  // 	}, 100)
  // }

  useEffect(() => {
    if (playerRef.current && isPlaying && lastPlayedTime > 0) {
      playerRef.current.seekTo(lastPlayedTime, "seconds");
    }
  }, [isPlaying, lastPlayedTime]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);

    return `${minutes < 10 ? "0" : ""}${minutes}:${sec < 10 ? "0" : ""}${sec}`;
  };

  useEffect(() => {
    if (volumeRef.current) {
      const maxWidth = volumeRef.current.offsetWidth - 8;
      const position = (volume / 1) * maxWidth;

      volumeRef.current.style.setProperty("--i-position", `${position}rem`);
    }
  }, [volume]);

  useEffect(() => {
    if (timeLineRef.current) {
      const maxWidth = timeLineRef.current.offsetWidth - 16;
      const position = timeLine * maxWidth;

      timeLineRef.current.style.setProperty("--i-position", `${position}rem`);
    }
  }, [timeLine]);

  return (
    <div
      ref={fullscreenRef}
      style={{background: "var(--greylight)"}}
      className={`${styles.videoPlayer} group`}
    >
      <div
        onClick={handlePlay}
        className={styles.video}
      >
        {isPlaying && !preview && !videoError && video.videoStream && (
          <ReactPlayer
            ref={playerRef}
            playing={isPlaying}
            url={video.videoStream}
            volume={volume}
            width={"100%"}
            height={"100%"}
            onEnded={handleVideoEnded}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onError={handleVideoError}
            progressInterval={100}
            playsinline
            config={{
              file: {
                forceVideo: true,
                attributes: {
                  controlsList: "nodownload",
                },
              },
            }}
          />
        )}

        {!isPlaying && !videoError && (
          <>
            {video.preview}

            <div
              onClick={handlePlay}
              className={styles.video_btn}
              style={
                !isMobile
                  ? {
                      width: `${playBtnSize}rem`,
                      height: `${playBtnSize}rem`,
                    }
                  : {
                      width: `60rem`,
                      height: `60rem`,
                    }
              }
            >
              <img
                src={playBtnImg}
                alt={"video-button"}
              />
            </div>
          </>
        )}

        {(videoError || !video.videoStream) && (
          <>
            {video.preview}

            <div className={styles.videoError}>
              <RootDesc>
                <span
                  style={{position: "relative", zIndex: "100"}}
                  className={"text-[var(--white)]"}
                >
                  {t("video.error")}
                </span>
              </RootDesc>
            </div>
          </>
        )}
      </div>

      {video.videoStream && !videoError && controlBar && (
        <>
          <div
            style={preview ? {opacity: "0"} : {opacity: "1"}}
            className={`${styles.videoControls} group-hover:bottom-[8rem]`}
          >
            <div className={"flex items-center gap-[16rem]"}>
              {isPlaying ? (
                <img
                  onClick={handlePlay}
                  className={"h-[38rem] w-[30rem]"}
                  src={pause}
                  alt={"pause"}
                />
              ) : (
                <img
                  onClick={handlePlay}
                  className={"h-[32rem] w-[30rem]"}
                  src={play}
                  alt={"play"}
                />
              )}

              <img
                className={"h-[14rem] w-[24rem]"}
                src={rewind}
                alt={"rewind"}
              />
            </div>

            <div className={"flex items-center gap-[16rem]"}>
              <div className={styles.time}>
                <time>{formatTime(currentTime)}</time>
              </div>

              <div
                ref={timeLineRef}
                className={styles.timeLine}
              >
                <label htmlFor={"time-line"}>
                  <input
                    min={0}
                    max={1}
                    step={0.01}
                    id={"time-line"}
                    type={"range"}
                    value={timeLine}
                    onChange={handleTimeLineChange}
                  />
                  <div
                    className={styles.bar}
                    style={{width: "calc(var(--i-position) + 8rem)"}}
                  >
                    <i
                      className={styles.thumb}
                      style={{left: "var(--i-position)"}}
                    />
                  </div>
                </label>
              </div>

              <div className={styles.time}>
                <time>{formatTime(videoDuration)}</time>
              </div>
            </div>

            <div className={"flex items-center gap-[8rem]"}>
              <img
                className={"h-[32rem] w-[32rem]"}
                src={settings}
                alt={"settings"}
              />

              <img
                onClick={handleClickSound}
                className={"h-[32rem] w-[32rem]"}
                src={sound}
                alt={"sound"}
              />

              <div
                ref={volumeRef}
                className={styles.volume}
              >
                <label htmlFor={"volume"}>
                  <input
                    min={0}
                    max={1}
                    step={0.05}
                    id={"volume"}
                    type={"range"}
                    value={volume}
                    onChange={handleVolumeChange}
                  />
                  <div
                    className={styles.bar}
                    style={{width: "calc(var(--i-position) + 4rem)"}}
                  >
                    <i
                      className={styles.thumb}
                      style={{left: "var(--i-position)"}}
                    />
                  </div>
                </label>
              </div>

              <img
                onClick={handleFullscreen}
                className={"h-[20rem] w-[34rem]"}
                src={fullscreen}
                alt={"fullscreen"}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
